import type * as THREE from 'three';
import { ASSET_CATALOG, type AssetName } from './catalog';
import { STUBS, interfaceOf } from './stubs';

export interface GltfLike {
  loadAsync(url: string): Promise<{ scene: THREE.Object3D }>;
}

export function assetUrl(name: AssetName, base = '/'): string {
  return `${base}assets/${ASSET_CATALOG[name].file}`;
}

/** A stalled request must not keep the scene black: after this the stub is used. */
export const ASSET_TIMEOUT_MS = 8_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`timed out after ${ms} ms`)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

/** Loads the Blender asset; on any failure, stall or interface mismatch keeps the stub. Never rejects. */
export async function loadAsset(
  name: AssetName,
  loader: GltfLike,
  base = '/',
  warn: (msg: string) => void = console.warn,
  timeoutMs = ASSET_TIMEOUT_MS,
): Promise<THREE.Object3D> {
  const stub = STUBS[name]();
  try {
    const gltf = await withTimeout(loader.loadAsync(assetUrl(name, base)), timeoutMs);
    const root = gltf.scene.getObjectByName(name);
    if (!root) throw new Error(`root node '${name}' missing`);
    const want = interfaceOf(stub);
    const got = interfaceOf(root);
    const wrong = Object.keys(want).filter((k) => JSON.stringify(got[k]) !== JSON.stringify(want[k]));
    if (wrong.length > 0) throw new Error(`interface mismatch on ${wrong.join(', ')}`);
    root.removeFromParent();
    return root;
  } catch (err) {
    warn(`[assets] ${name}: using stub (${err instanceof Error ? err.message : String(err)})`);
    return stub;
  }
}
